class CollectionAccessories extends HTMLElement {
  constructor() {
    super();
    this._categoryButtons = [];
    this._modelButtons = [];
    this._items = [];
    this._titleSyncFrame = null;
    this._lastGridWidth = -1;
    this._prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    this._onGridResize = this._onGridResize.bind(this);
  }

  connectedCallback() {
    this._teardownObservers();

    this._categoryScroll = this.querySelector(
      ".collection-accessories__categories-scroll"
    );
    this._categoryButtons = [
      ...this.querySelectorAll(
        "[data-category-tag].collection-accessories__category"
      ),
    ];
    this._modelButtons = [
      ...this.querySelectorAll("[data-model-tag].collection-accessories__model"),
    ];
    this._items = [...this.querySelectorAll("[data-accessories-item]")];
    this._emptyEl = this.querySelector("[data-accessories-empty]");
    this._grid = this.querySelector("[data-accessories-grid]");

    this._categoryButtons.forEach((button) => {
      button.addEventListener("click", () => this._onCategoryClick(button));
    });

    this._modelButtons.forEach((button) => {
      button.addEventListener("click", () => this._onModelClick(button));
    });

    const params = new URLSearchParams(window.location.search);
    const categoryTag = params.get("category");
    const modelTag = params.get("model");

    if (categoryTag) {
      const categoryButton = this._categoryButtons.find(
        (button) => button.dataset.categoryTag === categoryTag
      );
      if (categoryButton) {
        this._setActiveCategory(categoryButton, false);
      }
    }

    this._syncModelsForCategory(false);

    if (modelTag) {
      const modelButton = this._modelButtons.find(
        (button) =>
          button.dataset.modelTag === modelTag &&
          !button.classList.contains("is-hidden")
      );
      if (modelButton) {
        this._setActiveModel(modelButton, false);
      }
    }

    this._applyFilter(false);
    this._setupObservers();

    requestAnimationFrame(() => {
      this._scrollActiveTabIntoView(
        this._categoryScroll,
        this._getActiveCategoryButton()
      );
    });
  }

  disconnectedCallback() {
    this._teardownObservers();
  }

  _setupObservers() {
    if (this._grid && "ResizeObserver" in window) {
      this._resizeObserver = new ResizeObserver(this._onGridResize);
      this._resizeObserver.observe(this._grid);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!this.isConnected) return;
        this._scheduleTitleSync();
      });
    }
  }

  _teardownObservers() {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._lastGridWidth = -1;

    if (this._titleSyncFrame) {
      cancelAnimationFrame(this._titleSyncFrame);
      this._titleSyncFrame = null;
    }
  }

  _onGridResize(entries) {
    const width = Math.round(
      entries[0]?.contentRect?.width ?? this._grid?.clientWidth ?? 0
    );
    if (width === this._lastGridWidth) return;
    this._lastGridWidth = width;
    this._scheduleTitleSync();
  }

  _scheduleTitleSync() {
    if (this._titleSyncFrame) cancelAnimationFrame(this._titleSyncFrame);

    this._titleSyncFrame = requestAnimationFrame(() => {
      this._titleSyncFrame = requestAnimationFrame(() => {
        this._titleSyncFrame = null;
        this._syncTitleHeights();
      });
    });
  }

  _syncTitleHeights() {
    const titles = [];

    this._items.forEach((item) => {
      const title = item.querySelector(".accessories-card__title");
      if (!title) return;
      title.style.minHeight = "";
      if (!item.classList.contains("is-hidden")) titles.push(title);
    });

    if (!titles.length) return;

    const rows = [];

    titles.forEach((title) => {
      const top = Math.round(title.getBoundingClientRect().top);
      let row = rows.find((entry) => Math.abs(entry.top - top) <= 2);

      if (!row) {
        row = { top, titles: [] };
        rows.push(row);
      }

      row.titles.push(title);
    });

    rows.forEach((row) => {
      const maxHeight = Math.max(
        ...row.titles.map((title) => title.offsetHeight)
      );

      if (maxHeight <= 0) return;

      row.titles.forEach((title) => {
        title.style.minHeight = `${maxHeight}px`;
      });
    });
  }

  _onCategoryClick(button) {
    this._setActiveCategory(button, true);
    this._scrollActiveTabIntoView(this._categoryScroll, button);
    this._syncModelsForCategory(true);
    this._applyFilter(true);
  }

  _onModelClick(button) {
    this._setActiveModel(button, true);
    this._applyFilter(true);
  }

  _setActiveCategory(button, updateUrl) {
    this._categoryButtons.forEach((el) => {
      const isActive = el === button;
      el.classList.toggle("is-active", isActive);
      el.setAttribute("aria-selected", isActive ? "true" : "false");
    });
    this._activeCategory = button.dataset.categoryTag;

    if (updateUrl) {
      this._updateUrl();
    }
  }

  _setActiveModel(button, updateUrl) {
    this._modelButtons.forEach((el) => {
      const isActive = el === button;
      el.classList.toggle("is-active", isActive);
      el.setAttribute("aria-selected", isActive ? "true" : "false");
    });
    this._activeModel = button.dataset.modelTag;

    if (updateUrl) {
      this._updateUrl();
    }
  }

  _syncModelsForCategory(selectFirst = false) {
    const categoryTag = this._getActiveCategoryTag();
    let firstVisible = null;

    this._modelButtons.forEach((button) => {
      const matches = button.dataset.categoryTag === categoryTag;
      button.classList.toggle("is-hidden", !matches);
      button.setAttribute("aria-hidden", matches ? "false" : "true");

      if (matches && !firstVisible) {
        firstVisible = button;
      }
    });

    const activeModel = this._modelButtons.find(
      (button) =>
        button.classList.contains("is-active") &&
        !button.classList.contains("is-hidden")
    );

    if (!activeModel && firstVisible) {
      this._setActiveModel(firstVisible, false);
    } else if (selectFirst && firstVisible) {
      this._setActiveModel(firstVisible, false);
    }
  }

  _getActiveCategoryButton() {
    return this._categoryButtons.find((button) =>
      button.classList.contains("is-active")
    );
  }

  _getActiveModelTag() {
    const active = this._modelButtons.find(
      (button) =>
        button.classList.contains("is-active") &&
        !button.classList.contains("is-hidden")
    );
    return active?.dataset.modelTag || "";
  }

  _getActiveCategoryTag() {
    return (
      this._getActiveCategoryButton()?.dataset.categoryTag ||
      this._categoryButtons[0]?.dataset.categoryTag ||
      ""
    );
  }

  _scrollActiveTabIntoView(container, item) {
    if (!container || !item) return;

    const isMobile = window.matchMedia("(max-width: 850px)").matches;
    if (!isMobile) return;

    const behavior = this._prefersReducedMotion ? "auto" : "smooth";
    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const itemLeft = itemRect.left - containerRect.left + container.scrollLeft;
    const itemWidth = itemRect.width;
    const containerWidth = container.clientWidth;
    const maxScroll = Math.max(0, container.scrollWidth - containerWidth);

    let targetScroll = itemLeft - (containerWidth - itemWidth) / 2;
    targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

    container.scrollTo({ left: targetScroll, behavior });
  }

  _parseTagList(value) {
    if (!value) return [];
    return value.split("||").map((tag) => tag.trim()).filter(Boolean);
  }

  _applyFilter(updateUrl) {
    const categoryTag = this._getActiveCategoryTag();
    const modelTag = this._getActiveModelTag();
    let visibleCount = 0;

    this._items.forEach((item) => {
      const categories = this._parseTagList(item.dataset.categories);
      const models = this._parseTagList(item.dataset.models);
      const matchesCategory = categories.includes(categoryTag);
      const isCategoryWide = models.length === 0;
      const matchesModel = !modelTag || isCategoryWide || models.includes(modelTag);
      const isVisible = matchesCategory && matchesModel;

      item.classList.toggle("is-hidden", !isVisible);
      if (isVisible) visibleCount += 1;
    });

    if (this._emptyEl) {
      this._emptyEl.hidden = visibleCount > 0;
    }

    this._scheduleTitleSync();

    if (updateUrl) {
      this._updateUrl();
    }
  }

  _updateUrl() {
    const categoryTag = this._getActiveCategoryTag();
    const modelTag = this._getActiveModelTag();
    const url = new URL(window.location.href);

    if (categoryTag) {
      url.searchParams.set("category", categoryTag);
    } else {
      url.searchParams.delete("category");
    }

    if (modelTag) {
      url.searchParams.set("model", modelTag);
    } else {
      url.searchParams.delete("model");
    }

    window.history.replaceState({}, "", url.toString());
  }
}

if (!customElements.get("collection-accessories")) {
  customElements.define("collection-accessories", CollectionAccessories);
}
